import { Document, Packer } from 'docx'
import { DraftQuestionnaireEntry } from '../../data/model/questionnaire'
import { MarketOption } from '../../modules/Questionnaire/MarketVisibility/useProjectMarkets'
import { ResponseOptionsByQuestion } from '../../modules/Questionnaire/Questionnaire.slice'
import { flattenEntries } from '../../modules/Questionnaire/Questionnaire.utils'
import { MarketAudience } from '../../modules/Questionnaire/marketAudienceContext'
import { getLanguageLabel } from '../HelperFunctions'
import { sortBy } from '../array/sortBy'
import { countryLabel, flagEmoji, splitMarketName } from '../marketFlags'
import { createQuestions } from './createQuestions'
import { createQuestionsBehaviorSummary } from './createQuestionsBehaviorSummary'
import { createSummary } from './createSummary'
import { PreviewLink } from './exportMarkets'

export interface BuildSurveyDocParams {
  projectName: string
  // Exported language, if any. Suffixes both the summary's project name and
  // the filename (`<project>_<lang>.docx`).
  languageCode?: string
  surveyId: string
  countryName: string
  previewUrl: string
  questionnaireEntries: DraftQuestionnaireEntry[]
  questionnaireAudienceEntries: DraftQuestionnaireEntry[]
  marketAudience?: MarketAudience
  responseOptionsByQuestion: ResponseOptionsByQuestion
  includeAudienceQuestions: boolean
  // Authoritative multi-market flag (from project.projectType, resolved against
  // the freshly-queried project). Drives the summary's "Markets:" vs "Country:"
  // line and the audience-exclusion default upstream — never inferred from the
  // market list, which can be empty on a cold cache.
  isMultiMarket: boolean
  // The project's markets, used to render per-item "Hidden in: …" annotations
  // and the summary's market list. Empty for single-market surveys (no-op).
  allMarkets: MarketOption[]
  // Per-market preview links (multi-market). Absent → single `previewUrl`.
  previewLinks?: PreviewLink[]
  // Build the doc as respondents in this market see it: hidden entries,
  // options and rows are left out, master question numbers and answer codes
  // are kept, and the header and filename name the market and language.
  // Absent → the master doc.
  market?: MarketOption
}

const ILLEGAL_FILENAME_CHARS = /[\\/:*?"<>|]/g

/**
 * The market's country as plain text, no flag: the market label minus its
 * audience label ("United Kingdom — Boost" -> "United Kingdom"), else the full
 * name of its country code.
 */
export const marketCountryName = (market: MarketOption): string =>
  splitMarketName(market.label).countryPrefix.trim() ||
  (market.country ? countryLabel(market.country) : '')

/** "English (master)" for the master text, else the language's name. */
export const exportLanguageLabel = (languageCode?: string): string =>
  languageCode ? getLanguageLabel(languageCode) : 'English (master)'

const sanitizeFilename = (name: string): string =>
  name.replace(ILLEGAL_FILENAME_CHARS, '').replace(/\s+/g, ' ').trim()

// "Germany" / "United Kingdom Nat Rep": the market's country plus its
// audience label, which tells markets in the same country apart.
const marketFilenamePart = (market: MarketOption): string =>
  [marketCountryName(market), market.audienceLabel.trim()]
    .filter(Boolean)
    .join(' ')

/**
 * `<project> - <country>[ <survey label>] - <language>.docx`, unique per
 * market and language. Characters Windows/macOS reject in a file name are
 * dropped.
 */
export const marketDocFilename = (
  projectName: string,
  market: MarketOption,
  languageCode?: string
): string => {
  const name = sanitizeFilename(
    [projectName, marketFilenamePart(market), exportLanguageLabel(languageCode)]
      .filter(Boolean)
      .join(' - ')
  )
  return `${name}.docx`
}

/** `<project> - <country>[ <survey label>].zip`: every doc of one market. */
export const marketZipFilename = (
  projectName: string,
  market: MarketOption
): string => {
  const name = sanitizeFilename(
    [projectName, marketFilenamePart(market)].filter(Boolean).join(' - ')
  )
  return `${name}.zip`
}

/** `<project> - All survey documents.zip`: the Download all zip. */
export const downloadAllZipFilename = (projectName: string): string =>
  `${sanitizeFilename(`${projectName} - All survey documents`)}.zip`

/**
 * `<project> - Master.docx`: the master doc's name inside the Download all
 * zip, so it reads clearly next to the market docs. A plain master export
 * keeps `<project>.docx`.
 */
export const downloadAllMasterFilename = (projectName: string): string =>
  `${sanitizeFilename(`${projectName} - Master`)}.docx`

// "🇩🇪 Germany": the market's country with its flag, without the audience
// label (printed on its own line).
const formatMarketCountry = (market: MarketOption): string => {
  const flag = market.flag || (market.country ? flagEmoji(market.country) : '')
  const name = marketCountryName(market) || market.label
  return flag ? `${flag} ${name}` : name
}

export const buildSurveyDoc = async ({
  projectName,
  languageCode,
  surveyId,
  countryName,
  previewUrl,
  questionnaireEntries,
  questionnaireAudienceEntries,
  marketAudience,
  responseOptionsByQuestion,
  includeAudienceQuestions,
  isMultiMarket,
  allMarkets,
  previewLinks,
  market
}: BuildSurveyDocParams): Promise<{ blob: Blob; filename: string }> => {
  const marketSurveyId = market?.surveyId
  const filenameSuffix = languageCode ? `_${languageCode}` : ''
  const title = `${projectName}${filenameSuffix}`

  const pageContent = [
    ...(await createSummary({
      projectName: title,
      surveyId,
      countryName,
      previewUrl,
      isMultiMarket,
      markets: allMarkets,
      previewLinks,
      market: market && {
        name: formatMarketCountry(market),
        audienceLabel: market.audienceLabel.trim(),
        languageLabel: exportLanguageLabel(languageCode)
      }
    }))
  ]

  const sortedEntries: DraftQuestionnaireEntry[] = sortBy(
    questionnaireEntries,
    { key: 'position' }
  )

  const sortedAudienceEntries: DraftQuestionnaireEntry[] = sortBy(
    questionnaireAudienceEntries,
    { key: 'position' }
  )

  // Multi-market exports ship the shared (master) questionnaire only: audience
  // questions differ per market, so printing one market's screener would be
  // misleading. The audience entries are still passed to the survey-question
  // builder below so display-logic references to them keep resolving.
  if (includeAudienceQuestions) {
    const audienceQuestionContent = await createQuestions({
      audienceEntries: sortedAudienceEntries,
      marketAudience,
      responseOptionsByQuestion,
      allMarkets,
      marketSurveyId
    })
    pageContent.push(...audienceQuestionContent)
  }

  const questionContent = await createQuestions({
    entries: sortedEntries,
    audienceEntries: sortedAudienceEntries,
    marketAudience,
    responseOptionsByQuestion,
    allMarkets,
    marketSurveyId
  })
  pageContent.push(...questionContent)

  pageContent.push(
    ...createQuestionsBehaviorSummary(
      flattenEntries(sortedEntries),
      marketSurveyId
    )
  )

  const doc = new Document({
    styles: {
      paragraphStyles: [
        {
          id: 'Heading2',
          name: 'Heading 2',
          basedOn: 'Normal',
          next: 'Normal',
          quickFormat: true,
          run: {
            size: 22,
            font: 'Arial',
            bold: true
          }
        }
      ],
      default: {
        document: {
          run: {
            font: 'Arial',
            size: 22
          }
        }
      }
    },
    sections: [
      {
        children: pageContent
      }
    ]
  })

  const blob = await Packer.toBlob(doc)
  const filename = market
    ? marketDocFilename(projectName, market, languageCode)
    : `${title}.docx`
  return { blob, filename }
}
